import type { GameStateManager } from '../game/GameState';
import type { GameData, Researcher, GameSpeed } from '../game/types';

export class SilverBulletModal {
  private currentResearcher: Researcher | null = null;
  private selectedBullet: string | null = null;
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onConfirm: (bulletId: string) => void,
    private onCancel: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    // Only handle if modal is open
    if (this.container.classList.contains('hidden')) return;

    // Number keys 1-9 for selection
    if (event.code.startsWith('Digit') && event.code.length === 6) {
      const digit = parseInt(event.code[5]);
      if (digit >= 1 && digit <= 9) {
        event.preventDefault();
        event.stopPropagation();
        this.selectByIndex(digit - 1);
      }
    } else if (event.code === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.confirm();
    } else if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  private selectByIndex(index: number): void {
    const bullets = this.gameData.silverBullets;
    if (index >= 0 && index < bullets.length) {
      this.selectedBullet = bullets[index].id;
      this.render();
    }
  }

  private confirm(): void {
    const state = this.gameState.getState();
    const costs = this.gameData.costs;
    const canAfford = state.budget >= costs.silver_bullet_cost;

    if (this.selectedBullet && canAfford) {
      this.onConfirm(this.selectedBullet);
      this.hide();
    }
  }

  show(researcher: Researcher): void {
    this.currentResearcher = researcher;
    this.selectedBullet = null;
    this.render();
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    // Attach keyboard handler to document when modal opens
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    // Focus the modal content to ensure it can receive focus
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    
    // Remove keyboard handler when modal closes
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    this.onCancel();
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(): void {
    if (!this.currentResearcher) return;

    const strings = this.gameData.uiStrings;
    const costs = this.gameData.costs;
    const state = this.gameState.getState();
    const canAfford = state.budget >= costs.silver_bullet_cost;

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content" tabindex="0">
        <div class="modal-header">
          <h2>${strings.modals?.craft_title || 'Craft Silver Bullet'}</h2>
          <button class="modal-close" onclick="this.closest('.modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <div class="silver-bullet-selection">
            ${this.gameData.silverBullets.map(bullet => this.renderSilverBullet(bullet.id, bullet.effective_property)).join('')}
          </div>
          <div class="craft-summary">
            <div class="summary-item">
              <span>${strings.labels?.craft_cost || 'Craft Cost'}:</span>
              <span>${costs.silver_bullet_cost} ${strings.labels?.budget || 'Budget'}</span>
            </div>
            <div class="summary-item">
              <span>${strings.labels?.available_resources || 'Available'}:</span>
              <span>${state.budget} ${strings.labels?.budget || 'Budget'}</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-cancel" data-action="cancel">${strings.buttons?.cancel || 'Cancel'}</button>
          <button class="button-confirm ${!this.selectedBullet || !canAfford ? 'disabled' : ''}" 
                  ${!this.selectedBullet || !canAfford ? 'disabled' : ''}
                  data-action="confirm">${strings.buttons?.confirm || 'Confirm'}</button>
        </div>
      </div>
    `;

    // Attach event listeners
    this.gameData.silverBullets.forEach(bullet => {
      const option = this.container.querySelector(`[data-bullet-id="${bullet.id}"]`) as HTMLElement;
      if (option) {
        option.addEventListener('click', () => {
          this.selectedBullet = bullet.id;
          this.render();
        });
      }
    });

    // Attach button event listeners
    const cancelButton = this.container.querySelector('[data-action="cancel"]') as HTMLElement;
    if (cancelButton) {
      cancelButton.addEventListener('click', () => {
        const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
        if (closeButton) closeButton.click();
      });
    }

    const confirmButton = this.container.querySelector('[data-action="confirm"]') as HTMLElement;
    if (confirmButton) {
      confirmButton.addEventListener('click', () => {
        if (this.selectedBullet && canAfford) {
          this.onConfirm(this.selectedBullet);
          this.hide();
        }
      });
    }
  }

  private renderSilverBullet(id: string, effectiveProperty: string): string {
    const isSelected = this.selectedBullet === id;
    const strings = this.gameData.uiStrings;
    const propertyName = strings.properties?.[effectiveProperty.toLowerCase()] || effectiveProperty;
    const bullet = this.gameData.silverBullets.find(sb => sb.id === id);
    const displayName = bullet?.name || id;

    return `
      <div class="silver-bullet-option ${isSelected ? 'selected' : ''}" data-bullet-id="${id}" data-tooltip-key="silver_bullet.${id}">
        <div class="bullet-name">${displayName}</div>
        <div class="bullet-property">Effective vs: ${propertyName}</div>
      </div>
    `;
  }
}


